// Copyright (c) Docugami, Inc. All rights reserved.**/

import { Callout, ICalloutContentStyles, IChoiceGroupOption, Stack, TooltipHostBase } from "@fluentui/react";
import { DgButton } from "components/common/DgCommonStyledComponents";
import { dgFontFamily } from "components/common/Fonts";
import { greyText, homeGray, primaryTheme } from "components/shell/presentation/AppStyles";
import { DgChoiceGroup } from "components/shell/utils/DgChoiceGroup";
import { DgIconButton } from "components/shell/utils/DgIconButton";
import { dgIconNames } from "hooks/dgIcons";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { useState } from "react";
import { ReportFilterAxis, setFilterAxis, setFilters, setSearchString } from "store/reports/reportsSlice";
import styled from "styled-components";
import { FilterCheckbox, FilteredCountText, ReportFilterSearchField, useCheckboxFilters, useReportFiltersState } from "./ReportFilterComponents";

const SectionTitleText = styled.span`
    display: flex;
    font-family: ${dgFontFamily};
    font-weight: 700;
    font-size: 12px;
    line-height: 15px;
    align-items: center;
    text-align: center;
    color: ${homeGray};
`;

const ContentContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: 5px;
    height: fit-content;
    width: 100%;
`;

const AdditionalFilterContent = styled(Stack)`
    flex-wrap: wrap;
    margin: 5px;
    ${props => props.hidden && 'display: none'};
`

const calloutStyles: Partial<ICalloutContentStyles> = {
    root: {
        borderRadius: '10px',
        width: '300px'
    },
    calloutMain: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        borderRadius: '10px',
        padding: '20px',
        boxSizing: 'border-box',
        gap: '15px'
    }
}

export const ReportTableFilters = (): JSX.Element => {
    const searchString = useAppSelector(state => state.reportsSlice.searchString);
    const filterAxis = useAppSelector(state => state.reportsSlice.filterAxis);

    const { isActive, countText } = useReportFiltersState();
    const [showFilterPanel, setShowFilterPanel] = useState(isActive);

    const dispatch = useAppDispatch();

    const filters = useCheckboxFilters();

    function open() {
        setShowFilterPanel(true);
    }

    function clearState() {
        dispatch(setSearchString(''));
        dispatch(setFilters([]));
    }

    function close() {
        setShowFilterPanel(false);
        clearState();
    }

    const axisOptions: IChoiceGroupOption[] = [
        {
            key: ReportFilterAxis.Items,
            text: 'Items'
        },
        {
            key: ReportFilterAxis.Documents,
            text: 'Documents'
        }
    ]

    function onChange(key: string) {
        if (key === ReportFilterAxis.Documents) {
            dispatch(setFilterAxis(ReportFilterAxis.Documents))
        }
        else if (key === ReportFilterAxis.Items) {
            dispatch(setFilterAxis(ReportFilterAxis.Items))
        }
    }

    const filterTargetName = filterAxis === ReportFilterAxis.Items ? "Items" : "Documents";
    const filterSearchTargetName = filterAxis === ReportFilterAxis.Items ? "Item label" : "Document name";

    const showFilteredCount = !!countText && isActive;

    const targetId = 'filter-button';
    return (
        <>
            <TooltipHostBase content={showFilterPanel ? 'Clear Filters' : 'Filters'} styles={{ root: { display: 'flex', alignItems: 'center' } }}>
                {
                    showFilterPanel ? (
                        <DgIconButton
                            id={targetId}
                            iconName={dgIconNames.DgCloseIcon}
                            onClick={close}
                            height={14}
                            width={14}
                            color={greyText} />
                    ) : (
                        <DgIconButton
                            id={targetId}
                            iconName={dgIconNames.DgFilter}
                            onClick={open}
                            height={16}
                            width={16}
                            color={isActive ? primaryTheme.palette.themeSecondary : homeGray} />
                    )
                }
            </TooltipHostBase>
            {!showFilterPanel && showFilteredCount && <FilteredCountText>{countText}</FilteredCountText>}
            <Callout role="dialog"
                hidden={!showFilterPanel}
                onDismiss={() => setShowFilterPanel(false)}
                target={`#${targetId}`}
                isBeakVisible={false}
                styles={calloutStyles}>
                <Stack>
                    <SectionTitleText>FILTER BY:</SectionTitleText>
                    <DgChoiceGroup
                        hidden={!showFilterPanel}
                        selectedKey={filterAxis}
                        onChange={(option) => onChange(option?.key ?? '')}
                        options={axisOptions} />
                </Stack>
                <Stack tokens={{ childrenGap: '5px' }}>
                    <SectionTitleText>Search {filterTargetName}:</SectionTitleText>
                    <ContentContainer>
                        <ReportFilterSearchField
                            filterTargetLabel={filterSearchTargetName}
                            filterText={searchString}
                            onFilterTextChange={(value: string | undefined) => dispatch(setSearchString(value ?? ""))} />
                    </ContentContainer>
                </Stack>
                {
                    !!filters?.length && (
                        <AdditionalFilterContent horizontal>
                            {
                                filters.map(filter => (
                                    <Stack.Item
                                        key={filter.key}
                                        basis="50%"
                                        align="center"
                                        hidden={filter.hidden}>
                                        <FilterCheckbox filter={filter} />
                                    </Stack.Item>
                                ))
                            }
                        </AdditionalFilterContent>
                    )
                }
                {showFilteredCount && <FilteredCountText>{countText}</FilteredCountText>}
                <DgButton onClick={clearState} disabled={!isActive}>Reset</DgButton>
            </Callout>
        </>
    )
}